<!-- 确认预约 -->
<template>
    <div class="page">
        <van-nav-bar
            title="确认预约"
            left-arrow
            fixed
            placeholder
            safe-area-inset-top
            @click-left="goBack"
        />

        <div class="content">
            <!-- 会议室信息 -->
            <div class="room-card">
                <img class="room-cover" :src="room.cover" :alt="room.name" />
                <div class="room-body">
                    <div class="room-name">{{ room.name }}<template v-if="room.floor">({{ room.floor }})</template></div>
                    <div class="room-meta">
                        {{ room.location }} · {{ room.capacity }}人
                    </div>
                    <div class="room-meta">开放时间：{{ room.openTime }}</div>
                </div>
            </div>

            <!-- 预约信息 -->
            <div class="card">
                <div class="card-title">预约信息</div>
                <div class="field-row">
                    <span class="field-label">日期</span>
                    <span class="field-value">{{ dateText }}</span>
                </div>
                <div class="field-row">
                    <span class="field-label">时间</span>
                    <span class="field-value">{{ timeRange }}</span>
                </div>
                <div class="field-row field-row-input">
                    <span class="field-label">会议主题</span>
                    <input
                        v-model.trim="title"
                        class="field-input"
                        type="text"
                        maxlength="50"
                        placeholder="请输入会议主题(非必填)"
                    />
                </div>
            </div>

            <!-- 联系人 -->
            <div class="card">
                <div class="card-title">联系人</div>
                <div class="field-row">
                    <span class="field-label">手机号</span>
                    <span class="field-value field-value-readonly">{{ contactPhoneDisplay }}</span>
                </div>
                <div class="field-row field-row-input">
                    <span class="field-label">公司名称</span>
                    <input
                        v-model.trim="rentCompanyName"
                        class="field-input"
                        type="text"
                        maxlength="20"
                        placeholder="请输入公司名称(必填)"
                    />
                </div>
                <div class="field-row field-row-input">
                    <span class="field-label">公司室号</span>
                    <input
                        v-model.trim="rentCompanyHouseNo"
                        class="field-input"
                        type="text"
                        maxlength="10"
                        placeholder="请输入公司室号(必填)"
                    />
                </div>
            </div>

            <!-- 参会人 -->
            <div class="card card-attendees">
                <div class="card-head">
                    <div class="card-head-text">
                        <div class="card-title card-title-inline">参会人(非必填)</div>
                        <div class="card-subtitle">*填写后参会人可以查看预约记录及获得开门权限</div>
                    </div>
                    <button
                        class="add-attendee-btn"
                        type="button"
                        @click="addAttendee"
                    >
                        添加参会人
                    </button>
                </div>
                <div
                    v-for="(item, index) in attendeePhones"
                    :key="item._key"
                    class="field-row field-row-input attendee-row"
                >
                    <span class="field-label">手机号</span>
                    <input
                        v-model.trim="item.phone"
                        class="field-input"
                        type="tel"
                        inputmode="numeric"
                        maxlength="11"
                        placeholder="请输入参会人手机号(非必填)"
                    />
                    <button
                        v-if="attendeePhones.length > 1"
                        class="remove-attendee-btn"
                        type="button"
                        aria-label="删除参会人"
                        @click="removeAttendee(index)"
                    >
                        删除
                    </button>
                </div>
            </div>

            <p class="tip">提交即代表同意预约规则；预约开始前可随时取消。</p>
        </div>

        <!-- 底部操作栏 -->
        <div class="footer">
            <div class="fee-block">
                <div class="fee-label">应付</div>
                <div class="fee-value">¥{{ totalFee }}</div>
            </div>
            <button
                class="submit-btn"
                type="button"
                :disabled="submitting"
                @click="submitBooking"
            >提交预约</button>
        </div>
    </div>
</template>

<script>

import { createReservationRequest } from '@/request/api'
import { isUnauthorizedError } from '@/tools/utils'
import { Toast } from 'vant'

const CONTACT_COMPANY_CACHE_KEY = 'bookingContactCompany'

function getUserInfo() {
    try {
        return JSON.parse(localStorage.getItem('userInfo') || 'null')
    } catch (e) {
        return null
    }
}

function readContactCompanyCache() {
    try {
        const raw = JSON.parse(localStorage.getItem(CONTACT_COMPANY_CACHE_KEY) || 'null')
        return raw && typeof raw === 'object' ? raw : {}
    } catch (e) {
        return {}
    }
}

function saveContactCompanyCache(phone, rentCompanyName, rentCompanyHouseNo) {
    const key = String(phone || '').trim()
    if (!key) return
    const cache = readContactCompanyCache()
    cache[key] = {
        rentCompanyName: String(rentCompanyName || '').trim(),
        rentCompanyHouseNo: String(rentCompanyHouseNo || '').trim(),
    }
    try {
        localStorage.setItem(CONTACT_COMPANY_CACHE_KEY, JSON.stringify(cache))
    } catch (e) {
        // ignore quota / private mode errors
    }
}

function getCachedCompanyByPhone(phone) {
    const key = String(phone || '').trim()
    if (!key) return null
    const item = readContactCompanyCache()[key]
    if (!item || typeof item !== 'object') return null
    return {
        rentCompanyName: String(item.rentCompanyName || '').trim(),
        rentCompanyHouseNo: String(item.rentCompanyHouseNo || '').trim(),
    }
}

function formatDateText(dateKey) {
    if (!dateKey) return ''
    const parts = String(dateKey).split('-')
    if (parts.length !== 3) return dateKey
    const [y, m, d] = parts
    return `${Number(y)}年${Number(m)}月${Number(d)}日`
}

function pad(n) {
    return n < 10 ? `0${n}` : `${n}`
}

function normalizeTime(timeStr) {
    const raw = String(timeStr || '').trim()
    if (!raw) return ''
    const match = raw.match(/(\d{1,2}):(\d{2})/)
    if (!match) return raw
    return `${pad(Number(match[1]))}:${match[2]}`
}

function parseBookingTimeRange(timeRange) {
    const raw = String(timeRange || '').trim()
    if (!raw) return { start: '', end: '' }
    const parts = raw.split(/[—–-]/).map((s) => s.trim()).filter(Boolean)
    if (parts.length < 2) return { start: '', end: '' }
    return {
        start: parts[0],
        end: parts[parts.length - 1],
    }
}

function toDateTimeString(dateKey, timeStr) {
    const datePart = String(dateKey || '').trim()
    const timePart = normalizeTime(timeStr)
    if (!datePart || !timePart) return ''
    return `${datePart} ${timePart}:00`
}

let attendeeRowKey = 0
function createAttendeeRow(phone = '') {
    attendeeRowKey += 1
    return { _key: attendeeRowKey, phone }
}

export default {
    name: 'confirmBooking',
    data() {
        return {
            room: {
                id: '',
                name: '',
                floor: '',
                location: '',
                openTime: '09:00-18:00',
                cover: '',
                capacity: 8,
                price: 0,
                originPrice: 0,
                adminPhone: '',
            },
            dateKey: '',
            timeRange: '',
            hours: 1,
            title: '',
            contactPhone: '',
            rentCompanyName: '',
            rentCompanyHouseNo: '',
            attendeePhones: [createAttendeeRow()],
            submitting: false,
        }
    },
    computed: {
        dateText() {
            return formatDateText(this.dateKey)
        },
        totalFee() {
            const price = Number(this.room.price) || 0
            const hours = Number(this.hours) || 0
            return price * hours
        },
        contactPhoneDisplay() {
            if (!this.contactPhone) return '未绑定手机号'
            return this.contactPhone
        },
    },
    created() {
        this.initFromQuery()
        this.initContact()
    },
    methods: {
        goBack() {
            this.$router.back()
        },
        initFromQuery() {
            const q = this.$route.query || {}
            this.room = {
                id: q.id || '',
                name: q.name || '',
                floor: q.floor != null && String(q.floor).trim() !== ''
                    ? String(q.floor).trim()
                    : '',
                location: q.location || '',
                openTime: q.openTime || '09:00-18:00',
                cover: q.cover || '',
                capacity: Number(q.capacity) || 8,
                price: Number(q.price) || 0,
                originPrice: Number(q.originPrice) || 0,
                adminPhone: String(q.adminPhone || '').trim(),
            }
            this.dateKey = q.dateKey || ''
            this.timeRange = q.timeRange || ''
            this.hours = Number(q.hours) || 1
        },
        initContact() {
            const info = getUserInfo() || {}
            this.contactPhone = info.phone || ''
            const cached = getCachedCompanyByPhone(this.contactPhone)
            if (!cached) return
            if (cached.rentCompanyName) {
                this.rentCompanyName = cached.rentCompanyName
            }
            if (cached.rentCompanyHouseNo) {
                this.rentCompanyHouseNo = cached.rentCompanyHouseNo
            }
        },
        persistContactCompany() {
            saveContactCompanyCache(
                this.contactPhone,
                this.rentCompanyName,
                this.rentCompanyHouseNo,
            )
        },
        addAttendee() {
            this.attendeePhones.push(createAttendeeRow())
        },
        removeAttendee(index) {
            if (this.attendeePhones.length <= 1) return
            this.attendeePhones.splice(index, 1)
        },
        normalizedAttendeePhones() {
            return this.attendeePhones
                .map((row) => String(row.phone || '').trim())
                .filter(Boolean)
        },
        buildReservationTimes() {
            const { start, end } = parseBookingTimeRange(this.timeRange)
            const startTime = toDateTimeString(this.dateKey, start)
            const endTime = toDateTimeString(this.dateKey, end)
            if (!startTime || !endTime) return null
            return { startTime, endTime }
        },
        validate() {
            if (!this.room.id) {
                this.$toast && this.$toast('缺少会议室信息')
                return false
            }
            if (!this.dateKey || !this.timeRange) {
                this.$toast && this.$toast('缺少预约时间')
                return false
            }
            if (!this.buildReservationTimes()) {
                this.$toast && this.$toast('预约时间格式无效')
                return false
            }
            if (!/^1\d{10}$/.test(this.contactPhone)) {
                this.$toast && this.$toast('请先登录并绑定有效手机号')
                return false
            }
            if (!String(this.rentCompanyName || '').trim()) {
                this.$toast && this.$toast('请输入公司名称')
                return false
            }
            if (!String(this.rentCompanyHouseNo || '').trim()) {
                this.$toast && this.$toast('请输入公司室号')
                return false
            }
            const phones = this.normalizedAttendeePhones()
            const invalid = phones.find((p) => !/^1\d{10}$/.test(p))
            if (invalid) {
                this.$toast && this.$toast('请输入正确的参会人手机号')
                return false
            }
            const unique = new Set(phones)
            if (unique.size !== phones.length) {
                this.$toast && this.$toast('参会人手机号不能重复')
                return false
            }
            return true
        },
        submitBooking() {
            if (!this.validate() || this.submitting) return
            const times = this.buildReservationTimes()
            if (!times) return

            this.submitting = true
            Toast.loading({
                message: '提交中',
                forbidClick: true,
                duration: 0,
            })
            createReservationRequest({
                roomId: this.room.id,
                startTime: times.startTime,
                endTime: times.endTime,
                title: String(this.title || '').trim(),
                attendees: this.normalizedAttendeePhones().join(','),
                rentCompanyName: String(this.rentCompanyName || '').trim(),
                rentCompanyHouseNo: String(this.rentCompanyHouseNo || '').trim(),
            })
                .then(() => {
                    this.persistContactCompany()
                    this.$router.replace({
                        path: '/home/submittedReviews',
                        query: {
                            id: this.room.id,
                            name: this.room.name,
                            location: this.room.location,
                            dateKey: this.dateKey,
                            timeRange: this.timeRange,
                            hours: this.hours,
                            title: String(this.title || '').trim(),
                            contactPhone: this.contactPhone,
                            attendees: this.normalizedAttendeePhones().join(','),
                            adminPhone: this.room.adminPhone || '',
                        },
                    })
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    const msg = (err && (err.message || err.msg)) || '提交预约失败'
                    this.$toast && this.$toast(msg)
                })
                .finally(() => {
                    this.submitting = false
                    Toast.clear()
                })
        },
    },
}
</script>

<style scoped>
.page {
    min-height: 100vh;
    background: #eef3f9;
    box-sizing: border-box;
    padding-bottom: calc(1.36rem + env(safe-area-inset-bottom));
}

.content {
    padding: 0.28rem 0.28rem 0.32rem;
    box-sizing: border-box;
}

.room-card {
    display: flex;
    align-items: flex-start;
    padding: 0.24rem;
    margin-bottom: 0.24rem;
    background: #ffffff;
    border-radius: 0.24rem;
    box-shadow: 0 0.06rem 0.2rem rgba(40, 90, 160, 0.06);
    box-sizing: border-box;
}

.room-cover {
    width: 1.48rem;
    height: 1.48rem;
    border-radius: 0.16rem;
    object-fit: cover;
    flex-shrink: 0;
    background: #d9e6f5;
}

.room-body {
    flex: 1;
    min-width: 0;
    margin-left: 0.2rem;
}

.room-location {
    font-size: 0.22rem;
    color: #8a9bb0;
    line-height: 1.3;
}

.room-name {
    margin-top: 0.08rem;
    font-size: 0.32rem;
    font-weight: 700;
    color: #1e3a5f;
    line-height: 1.3;
}

.room-meta {
    margin-top: 0.1rem;
    font-size: 0.22rem;
    color: #303133;
    line-height: 1.45;
    word-break: break-all;
}

.card {
    padding: 0.28rem 0.28rem 0.08rem;
    margin-bottom: 0.24rem;
    background: #ffffff;
    border-radius: 0.24rem;
    box-shadow: 0 0.06rem 0.2rem rgba(40, 90, 160, 0.06);
    box-sizing: border-box;
}

.card-title {
    margin-bottom: 0.08rem;
    font-size: 0.3rem;
    font-weight: 700;
    color: #1e3a5f;
}

.card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 0.08rem;
    gap: 0.16rem;
}

.card-head-text {
    flex: 1;
    min-width: 0;
}

.card-title-inline {
    margin-bottom: 0;
}

.card-subtitle {
    margin-top: 0.06rem;
    font-size: 0.22rem;
    color: #e2554a;
    line-height: 1.4;
}

.add-attendee-btn {
    flex-shrink: 0;
    border: none;
    background: transparent;
    padding: 0;
    font-size: 0.26rem;
    color: #2b7de9;
    cursor: pointer;
}

.add-attendee-btn:active {
    opacity: 0.75;
}

.field-value-readonly {
    color: #5a6f8a;
}

.attendee-row {
    gap: 0.12rem;
}

.remove-attendee-btn {
    flex-shrink: 0;
    border: none;
    background: transparent;
    padding: 0 0 0 0.08rem;
    font-size: 0.26rem;
    color: #e2554a;
    cursor: pointer;
}

.remove-attendee-btn:active {
    opacity: 0.75;
}

.card-attendees .field-row:last-child {
    border-bottom: none;
}

.field-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 0.96rem;
    border-bottom: 1px solid #eef2f7;
    box-sizing: border-box;
}

.card .field-row:last-child {
    border-bottom: none;
}

.field-label {
    flex-shrink: 0;
    font-size: 0.28rem;
    color: #5a6f8a;
}

.field-value {
    font-size: 0.28rem;
    color: #1e3a5f;
    text-align: right;
}

.field-row-input {
    gap: 0.2rem;
}

.field-input {
    flex: 1;
    min-width: 0;
    height: 0.72rem;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.28rem;
    color: #1e3a5f;
    text-align: right;
}

.field-input::placeholder {
    color: #b0bccb;
}

.tip {
    margin: 0.08rem 0.08rem 0;
    font-size: 0.22rem;
    color: #9aa8bc;
    line-height: 1.5;
}

.footer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.24rem;
    padding: 0.2rem 0.32rem calc(0.2rem + env(safe-area-inset-bottom));
    background: #ffffff;
    box-shadow: 0 -0.04rem 0.2rem rgba(30, 58, 95, 0.06);
    box-sizing: border-box;
}

.fee-block {
    flex-shrink: 0;
}

.fee-label {
    font-size: 0.22rem;
    color: #8a9bb0;
    line-height: 1.3;
}

.fee-value {
    margin-top: 0.04rem;
    font-size: 0.4rem;
    font-weight: 700;
    color: #2b7de9;
    line-height: 1.2;
}

.submit-btn {
    flex: 1;
    height: 0.96rem;
    max-width: 4.4rem;
    border: none;
    border-radius: 0.48rem;
    background: #2b7de9;
    color: #ffffff;
    font-size: 0.32rem;
    font-weight: 600;
    cursor: pointer;
}

.submit-btn:disabled {
    background: #a8c7ef;
    cursor: not-allowed;
}

.submit-btn:not(:disabled):active {
    opacity: 0.9;
}

.page ::v-deep .van-nav-bar {
    background: #ffffff;
}

.page ::v-deep .van-nav-bar .van-nav-bar__title {
    color: #1e3a5f;
    font-weight: 600;
}

.page ::v-deep .van-nav-bar .van-icon {
    color: #2b7de9;
}
</style>
